/**
 * Market concentration: the Herfindahl–Hirschman Index (HHI), concentration ratios, and
 * the merger screen in the 2023 US Merger Guidelines (DOJ and FTC, Guideline 1).
 *
 * The HHI is the sum of the squares of the firms' market shares, with shares written as
 * percentage points, so it runs from near 0 (many tiny firms) to 10,000 (one firm). A
 * merger of two firms with shares a and b raises it by 2ab. The 2023 Guidelines treat a
 * market with an HHI above 1,800 as highly concentrated and an increase of more than 100
 * points as significant. A merger that produces a highly concentrated market and raises
 * the HHI by more than 100 points is presumed to substantially lessen competition, and
 * so is one that creates a firm with a share above 30 percent and raises the HHI by more
 * than 100 points.
 */

/** HHI above this is "highly concentrated" (2023 Merger Guidelines). */
export const HIGHLY_CONCENTRATED = 1800;
/** An HHI increase above this is "significant" (2023 Merger Guidelines). */
export const SIGNIFICANT_INCREASE = 100;
/** A merged firm above this share (percent) also triggers the presumption. */
export const SHARE_THRESHOLD = 30;

/** Remove floating-point dust so a result of exactly 100 compares as exactly 100. */
function clean(value: number): number {
  return Math.round(value * 1e9) / 1e9;
}

/** Shares in percentage points from the values given: shares as typed, or each firm's part of the total. */
export function toShares(
  values: readonly number[],
  kind: "shares" | "sales",
  rescale: boolean
): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (kind === "sales" || rescale) {
    return total > 0
      ? values.map((v) => (v / total) * 100)
      : values.map(() => 0);
  }
  return [...values];
}

/** The HHI: the sum of squared shares, in percentage points. */
export function hhi(shares: readonly number[]): number {
  return clean(shares.reduce((sum, s) => sum + s * s, 0));
}

/** The combined share of the `k` largest firms, in percentage points. */
export function concentrationRatio(
  shares: readonly number[],
  k: number
): number {
  const top = [...shares].sort((a, b) => b - a).slice(0, k);
  return clean(top.reduce((sum, s) => sum + s, 0));
}

/** How many equal-sized firms would give the same HHI: 10,000 ÷ HHI. */
export function equivalentFirms(index: number): number | null {
  return index > 0 ? 10_000 / index : null;
}

export type Band = "highly" | "notHighly";

/** The 2023 Guidelines' only HHI threshold for a market's concentration. */
export function band(index: number): Band {
  return clean(index) > HIGHLY_CONCENTRATED ? "highly" : "notHighly";
}

export interface Merger {
  /** 2 × a × b */
  increase: number;
  before: number;
  after: number;
  mergedShare: number;
  /** After > 1,800 and increase > 100. */
  highlyConcentratedProng: boolean;
  /** Merged share > 30 and increase > 100. */
  shareProng: boolean;
  presumed: boolean;
}

/** The effect of merging firms `i` and `j` (indices into `shares`). */
export function merger(
  shares: readonly number[],
  i: number,
  j: number
): Merger {
  const before = hhi(shares);
  const increase = clean(2 * shares[i] * shares[j]);
  const after = clean(before + increase);
  const mergedShare = clean(shares[i] + shares[j]);
  const significant = increase > SIGNIFICANT_INCREASE;
  const highlyConcentratedProng = after > HIGHLY_CONCENTRATED && significant;
  const shareProng = mergedShare > SHARE_THRESHOLD && significant;
  return {
    increase,
    before,
    after,
    mergedShare,
    highlyConcentratedProng,
    shareProng,
    presumed: highlyConcentratedProng || shareProng,
  };
}
